import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  GraduationCap, Clock, MapPin, CheckCircle2, XCircle, FileQuestion,
  Award, PartyPopper, CalendarDays,
} from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/helpers";

export default function MyLearning() {
  const [enrollments, setEnrollments] = useState(null);
  const [quiz, setQuiz] = useState(null);

  const load = async () => {
    const { data } = await api.get("/enrollments/me");
    setEnrollments(data);
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.put(`/enrollments/${id}/cancel`);
      toast.success("Enrollment cancelled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openQuiz = async (courseId, courseTitle) => {
    try {
      const { data } = await api.get(`/courses/${courseId}/quiz`);
      if (!data) return toast.info("No assessment available for this course");
      setQuiz({ ...data, courseTitle });
    } catch (e) { toast.error("Could not load quiz"); }
  };

  if (!enrollments) return <Loading />;

  const active = enrollments.filter((e) => e.status !== "completed");
  const completed = enrollments.filter((e) => e.status === "completed");

  return (
    <div>
      <PageHeader title="My Learning" subtitle="Track your enrollments, complete assessments and earn certificates." testid="my-learning-header" />

      {enrollments.length === 0 ? (
        <EmptyState message="You haven't registered for any sessions yet. Browse the catalog to get started." icon={GraduationCap} />
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="font-heading text-lg font-semibold mb-3">In Progress</h2>
            {active.length === 0 ? <p className="text-sm text-muted-foreground">Nothing in progress.</p> : (
              <div className="grid md:grid-cols-2 gap-4">
                {active.map((e) => <EnrollmentCard key={e.id} e={e} onCancel={cancel} onQuiz={openQuiz} />)}
              </div>
            )}
          </section>
          {completed.length > 0 && (
            <section>
              <h2 className="font-heading text-lg font-semibold mb-3">Completed</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {completed.map((e) => <EnrollmentCard key={e.id} e={e} onCancel={cancel} onQuiz={openQuiz} />)}
              </div>
            </section>
          )}
        </div>
      )}

      {quiz && <QuizModal quiz={quiz} onClose={() => setQuiz(null)} onDone={load} />}
    </div>
  );
}

function EnrollmentCard({ e, onCancel, onQuiz }) {
  const s = e.session;
  const done = e.status === "completed";
  return (
    <Card className="p-5 stagger-in" data-testid={`enrollment-card-${e.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{e.course?.category}</span>
          </div>
          <h3 className="font-heading font-semibold leading-snug">{e.course?.title}</h3>
        </div>
        <StatusBadge status={e.status} testid={`enrollment-status-${e.id}`} />
      </div>

      {s && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(s.start)}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTime(s.start)}</span>
          <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.location}</span>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium">{done ? "100%" : e.attendance === "present" ? "75%" : "25%"}</span>
        </div>
        <Progress value={done ? 100 : e.attendance === "present" ? 75 : 25} className="h-2" />
      </div>

      {done && e.score != null && (
        <div className="mt-3 flex items-center gap-1.5 text-sm text-emerald-600"><Award className="h-4 w-4" />Scored {e.score}% · Certificate issued</div>
      )}

      <div className="flex gap-2 mt-4">
        {!done && <Button size="sm" variant="outline" onClick={() => onQuiz(e.course_id, e.course?.title)} data-testid={`take-quiz-${e.id}-button`}><FileQuestion className="h-4 w-4 mr-1.5" />Take Assessment</Button>}
        {!done && e.status !== "waitlisted" && <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-600" onClick={() => onCancel(e.id)} data-testid={`cancel-enrollment-${e.id}-button`}>Cancel</Button>}
        {!done && e.status === "waitlisted" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onCancel(e.id)}>Leave waitlist</Button>}
      </div>
    </Card>
  );
}

function QuizModal({ quiz, onClose, onDone }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const arr = quiz.questions.map((_, i) => (answers[i] != null ? answers[i] : -1));
    setSubmitting(true);
    try {
      const { data } = await api.post(`/courses/${quiz.course_id}/quiz/submit`, { answers: arr });
      setResult(data);
      if (data.passed && data.awaiting_attendance) { toast.success(`Passed with ${data.score}%! Your certificate will be issued once your attendance is confirmed.`); onDone(); }
      else if (data.passed) { toast.success(`Passed with ${data.score}%! Certificate issued.`); onDone(); }
      else toast.error(`Scored ${data.score}%. Try again to pass.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const allAnswered = quiz.questions.every((_, i) => answers[i] != null);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{quiz.title}</DialogTitle></DialogHeader>
        {result ? (
          <div className="text-center py-6">
            <div className={`h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 ${result.passed ? "bg-emerald-100 dark:bg-emerald-500/15" : "bg-red-100 dark:bg-red-500/15"}`}>
              {result.passed ? <PartyPopper className="h-8 w-8 text-emerald-600" /> : <XCircle className="h-8 w-8 text-red-600" />}
            </div>
            <p className="font-heading text-3xl font-bold">{result.score}%</p>
            <p className="text-sm text-muted-foreground mt-1">{result.correct} of {result.total} correct</p>
            <p className={`mt-3 font-medium ${result.passed ? "text-emerald-600" : "text-red-600"}`}>{result.passed ? (result.awaiting_attendance ? "Passed — certificate issued once attendance is confirmed" : "Passed — certificate unlocked!") : "Not passed yet"}</p>
          </div>
        ) : (
          <div className="space-y-5">
            {quiz.questions.map((q, qi) => (
              <div key={q.id} data-testid={`quiz-question-${qi}`}>
                <p className="text-sm font-medium mb-2">{qi + 1}. {q.text}</p>
                <div className="space-y-1.5">
                  {q.options.map((opt, oi) => (
                    <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))} data-testid={`quiz-q${qi}-option-${oi}`}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${answers[qi] === oi ? "border-primary bg-accent/50 font-medium" : "border-border hover:bg-accent/30"}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          {result ? <Button onClick={onClose} data-testid="close-quiz-button">Close</Button>
            : <Button onClick={submit} disabled={!allAnswered || submitting} data-testid="submit-quiz-button">{submitting ? "Submitting..." : "Submit Assessment"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
